<script setup>
import { ref, computed, nextTick } from 'vue'
import AlertNotification from '../../AlertNotification.vue'
import BcsSearchDropdown from '../../BcsSearchDropdown.vue'
import EmailInput from '../../Reminders/EmailInput.vue'

const props = defineProps({ 
  employersForStep2: { type: Array, required: true },
  employerEmailsState: { type: Object, required: true }
})

const emit = defineEmits(['update-employer-emails'])

// Local state
const employerSearch = ref('')
const selectedEmployer = ref(null)
const newEmailInput = ref('')
const searchDropdownRef = ref(null)

// Track if we should show animation (only for the moment of change)
const showingAnimation = ref(false)
// Previous state to detect direction of change
const wasModifiedBefore = ref(false)

function handleSearch(payload) {
  employerSearch.value = payload.term
}

function handleClearSearch() {
  employerSearch.value = ''
}

// Track if current selected employer just had a change (for animation)
const showSavedAnimation = ref(false)

function onSelectEmployer(employer) { 
  newEmailInput.value = ''
  showSavedAnimation.value = false
  showingAnimation.value = false
  selectedEmployer.value = employer
  wasModifiedBefore.value = isEmployerModified(employer.employerId)
}

// Check if an employer is in modified state (not in original state)
function isEmployerModified(employerId) {
  const state = props.employerEmailsState[employerId]
  if (!state) return false
  
  // Original state: all originalEmails selected AND no additionalEmails
  const allOriginalSelected = state.originalEmails.every(email => state.selectedEmails.has(email))
  const noAdditionalEmails = state.additionalEmails.length === 0
  
  // If in original state, not modified
  return !(allOriginalSelected && noAdditionalEmails)
}

// Check if current selected employer is modified
const isCurrentEmployerModified = computed(() => {
  if (!selectedEmployer.value) return false
  return isEmployerModified(selectedEmployer.value.employerId)
})

// Trigger animation when state changes
function triggerStateChangeAnimation() {
  if (!selectedEmployer.value) return  
  
  // Only animate if state actually changed
  if (isCurrentEmployerModified !== wasModifiedBefore.value) {
    showingAnimation.value = true
    showSavedAnimation.value = true
    
    setTimeout(() => {
      showSavedAnimation.value = false
      showingAnimation.value = false
      wasModifiedBefore.value = isCurrentEmployerModified
    }, 1000)
  }
}

const filteredEmployers = computed(() => {
  const term = employerSearch.value.trim().toLowerCase()
  if (!term) return props.employersForStep2
  return props.employersForStep2.filter(e => e.employerName.toLowerCase().includes(term))
})

// Get email state for current employer from parent's centralized state
const currentEmailState = computed(() => {
  if (!selectedEmployer.value) return null
  return props.employerEmailsState[selectedEmployer.value.employerId] || null
})

// Get original emails for checkboxes (contactEmails from employer)
const originalEmailsList = computed(() => {
  if (!currentEmailState.value) return []
  return currentEmailState.value.originalEmails
})

// Get additional emails for tags
const additionalEmailsList = computed(() => {
  if (!currentEmailState.value) return []
  return currentEmailState.value.additionalEmails
})

// Check if there's unsaved input (something typed but not yet added)
const hasUnsavedInput = computed(() => {
  return newEmailInput.value.trim().length > 0
})

// Check if an email is selected for current employer
function isEmailSelected(email) {
  return currentEmailState.value?.selectedEmails.has(email) ?? true
}

// Toggle email selection and emit to parent
function toggleEmailSelection(email) {
  if (!selectedEmployer.value || !currentEmailState.value) return
  
  const newSelectedEmails = new Set(currentEmailState.value.selectedEmails)
  newSelectedEmails.has(email) ? newSelectedEmails.delete(email) : newSelectedEmails.add(email)
  
  emit('update-employer-emails', selectedEmployer.value.employerId, {
    selectedEmails: newSelectedEmails,
    additionalEmails: currentEmailState.value.additionalEmails
  })
  nextTick(() => triggerStateChangeAnimation())
}

// Handle updates from EmailInput component
function onAdditionalEmailsUpdate(newEmails) {
  if (!selectedEmployer.value || !currentEmailState.value) return
  
  emit('update-employer-emails', selectedEmployer.value.employerId, {
    selectedEmails: currentEmailState.value.selectedEmails,
    additionalEmails: newEmails
  })
  nextTick(() => triggerStateChangeAnimation())
}
</script>

<template>
  <div id="step2RecipientsContainer" class="mb-4">
    <!-- Alert notification -->
    <div id="step2InfoAlert" class="mb-3">
      <AlertNotification typeAlert="info-soft" :showAlert="true" :showCloseButton="false">
        <strong><em>If you skip recipient confirmation, reminders will be sent to the primary email addresses for each employer, and you may proceed straight to Step 3.</em></strong>
      </AlertNotification>
    </div>

    <!-- Main card container -->
    <div id="step2RecipientsCard" class="card border">
      <!-- Card header -->
      <div id="step2SelectedEmployersHeader" class="card-header bg-light border-bottom py-3 px-4 header-20 d-flex justify-content-between align-items-center">
        <strong>{{ employersForStep2.length }} Selected Employers</strong>
        <span class="text-12 text-gray-600">
          {{ hasUnsavedInput ? 'Your changes will be auto-saved when made.' : 'Saved just now' }}
        </span>
      </div>

      <!-- Card body with two panels -->
      <div class="card-body p-0">
        <div class="d-flex">
          <!-- Left panel: Employer list -->
          <div id="step2EmployersListPanel" class="employers-list-panel border-end">
            <!-- Search box -->
            <div class="p-3 border-bottom">
              <BcsSearchDropdown 
                id="step2EmployerSearch"
                ref="searchDropdownRef"
                @search="handleSearch" 
                @clear="handleClearSearch"
                :search-fields="['employerName']"
                :array-term="'employerName'"
                :showResults="false" 
                :placeholder="'Search Employer by Name'"
                :items="employersForStep2" 
                :search-button-text="''" 
              />
            </div>

            <!-- Employers scroll list -->
            <div id="step2EmployersScrollList" class="employers-scroll">
              <div 
                v-for="emp in filteredEmployers" 
                :key="emp.employerId" 
                :id="`step2EmployerRow-${emp.employerId}`"
                class="employer-row px-3 py-2" 
                :class="{ 
                  'active': emp.employerId === selectedEmployer?.employerId && !isCurrentEmployerModified && !showingAnimation,
                  'modified': emp.employerId === selectedEmployer?.employerId && isCurrentEmployerModified && !showingAnimation,
                  'animating-to-modified': emp.employerId === selectedEmployer?.employerId && showSavedAnimation && isCurrentEmployerModified,
                  'animating-to-original': emp.employerId === selectedEmployer?.employerId && showSavedAnimation && !isCurrentEmployerModified
                }" 
                @click="onSelectEmployer(emp)"
              >
                <div class="d-flex justify-content-between align-items-center w-100">
                  <span class="text-14 employer-name-text" :class="{ 'fw-bold': emp.employerId === selectedEmployer?.employerId }">
                    {{ emp.employerName }}
                  </span>
                  <span class="text-14 text-nowrap">{{
                  (props.employerEmailsState[emp.employerId]?.selectedEmails.size || 0)
                  + (props.employerEmailsState[emp.employerId]?.additionalEmails.length
                  || 0) }} Set</span>
                </div>
              </div>
              <div v-if="filteredEmployers.length === 0" class="text-center text-14 py-4 text-muted">
                No employers match your search.
              </div>
            </div>
          </div>

          <!-- Right panel: Email recipients -->
          <div id="step2RecipientsPanel" class="recipients-panel flex-grow-1">
            <!-- Empty state when no employer selected -->
            <div v-if="!selectedEmployer" class="d-flex align-items-center justify-content-center h-100 text-muted">
              <div class="text-center">
                <i class="bi bi-person-lines-fill" style="font-size: 2.5rem;"></i>
                <p class="mt-2 text-14">Select an employer to view and edit recipients</p>
              </div>
            </div>

            <!-- Employer recipients when selected -->
            <div v-else class="p-4">
              <!-- Employer name header -->
              <p class="mb-2 header-20"><strong>{{ selectedEmployer.employerName }}</strong></p>
              <p class="text-14 text-gray-700 mb-4">Review or edit the employer recipients for this employer.</p>
              <p class="text-14 pt-1.5 mb-2.5"><strong>Select Recipients from Employer Contact List</strong></p>

              <!-- Email checkboxes -->
              <div id="step2EmailCheckboxesList" class="emails-list mb-4">
                <div 
                  v-for="email in originalEmailsList" 
                  :key="email" 
                  class="form-check mb-2"
                >
                  <input 
                    class="form-check-input" 
                    type="checkbox" 
                    :id="`email-${selectedEmployer.employerId}-${email}`"
                    :checked="isEmailSelected(email)"
                    @change="toggleEmailSelection(email)"
                  />
                  <label 
                    class="form-check-label text-16" 
                    :class="{ 'text-primary fw-bold': isEmailSelected(email) }"
                    :for="`email-${selectedEmployer.employerId}-${email}`"
                  >
                    {{ email }}
                  </label>
                </div>
                <div v-if="originalEmailsList.length === 0" class="text-muted text-14">
                  No contact emails available for this employer.
                </div>
              </div>

              <!-- Add More Recipients section -->
              <div id="step2AddRecipientsSection" class="add-recipients-section">
                <h6 class="mb-1 pt-1 text-14"><strong>Add More Recipients</strong></h6>
                <p class="text-14 text-gray-700 mb-2">
                  Type an email and press enter or separate with commas. Enter as many emails as needed.
                </p>

                <EmailInput 
                  id="step2TagsInputContainer"
                  class="mb-3"
                  :modelValue="additionalEmailsList" 
                  @update:modelValue="onAdditionalEmailsUpdate"
                  v-model:newEmail="newEmailInput"
                  :placeholder="additionalEmailsList.length === 0 ? 'Enter additional employer email addresses' : ''"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>

/* Left panel */
.employers-list-panel {
  width: 40%;
  min-width: 280px;
  max-width: 427px;
  display: flex;
  flex-direction: column;
  border-color: var(--bs-gray-200);
}

.employers-scroll {
  overflow-y: auto;
  max-height: 380px;
  flex: 1;
}

.employer-row {
  cursor: pointer;
  border-bottom: 1px solid #f0f0f0;
  transition: background-color 0.15s ease;
}

.employer-row:hover {
  background-color: var(--bs-gray-100);
}

.employer-row.active {
  background-color: var(--bs-primary);
  color: white;
}

.employer-row.modified {
  background-color: var(--green-060);
  color: var(--green-500);
}

.employer-row.animating-to-modified {
  animation: fadeToGreen 0.4s ease-out forwards;
}

.employer-row.animating-to-original {
  animation: fadeToPrimary 0.4s ease-out forwards;
}

@keyframes fadeToGreen {
  0% {
    background-color: var(--bs-primary);
    color: white;
  }
  100% {
    background-color: var(--green-060);
    color: var(--green-500);
  }
}

@keyframes fadeToPrimary {
  0% {
    background-color: var(--green-060);
    color: var(--green-500);
  }
  100% {
    background-color: var(--bs-primary);
    color: white;
  }
}

/* Right panel */
.recipients-panel {
  min-height: 430px;
}

</style>
